import { useEffect, useRef, useState } from 'react'
import { ChevronRight, Clock, Headset, MessageSquare, Mic, MicOff, MoreHorizontal, Phone, PhoneOff, RotateCw, Sparkles, X } from 'lucide-react'
import { useClientCall, type ClientCallAiHandoff, type ClientCallErrorCode } from '../../modules/softphone/useClientCall.js'
import { loadGuestIdentity } from '../../utils/clientGuestIdentity.js'
import { RatingPopup } from '@kc3/chat-widget'

interface ClientCallWidgetProps {
  tenantSlug: string
  perInteractionEnabled: boolean
  ratingScale: number
  onClose: () => void
  onContinueChat: () => void
  /** Started by the AI voice assistant's "transfer to a human". */
  aiHandoff?: ClientCallAiHandoff | null | undefined
}

const ERROR_COPY: Record<ClientCallErrorCode, string> = {
  NO_AGENTS: 'All agents are busy. You can try after some time.',
  MIC_DENIED: 'Microphone access is required to make a support call.',
  WS_UNREACHABLE: 'Unable to reach our call service right now.',
  REGISTER_FAILED: 'Unable to connect to an agent.',
  TIMEOUT: 'No one answered. Please try again.',
  AGENT_REJECTED: 'Unable to connect to an agent.',
  UNEXPECTED_TERMINATION: 'The call ended unexpectedly.',
  UNSUPPORTED_BROWSER: 'Voice calls are not supported in this browser.',
  CALL_UNAVAILABLE: 'Voice calls are not available right now. Please try again later.',
}

function formatDuration(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

export function ClientCallWidget({ tenantSlug, perInteractionEnabled, ratingScale, onClose, onContinueChat, aiHandoff }: ClientCallWidgetProps) {
  const call = useClientCall(tenantSlug, loadGuestIdentity(tenantSlug), aiHandoff)
  const [ratingDismissed, setRatingDismissed] = useState(false)

  const startedRef = useRef(false)
  useEffect(() => {
    if (call.state === 'IDLE' && !startedRef.current) {
      startedRef.current = true
      console.log('[kc3-call-widget] startCall triggered — tenantSlug:', tenantSlug)
      void call.startCall()
    }
  }, [call.state, call.startCall])

  return (
    <div className="kc3-widget-panel">
      {/* voice call audio, not a media file — no caption track applies */}
      <audio ref={call.remoteAudioRef} autoPlay />
      <button type="button" className="kc3-widget-close" onClick={onClose} aria-label="Close">
        <X className="h-3 w-3" />
      </button>
      <div className="kc3-widget h-full overflow-hidden flex flex-col">
        <div className="flex items-center gap-3 px-5 py-5 bg-gradient-to-br from-blue-600 to-indigo-600 text-white flex-shrink-0">
          <div className="h-11 w-11 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
            <Phone className="h-6 w-6" />
          </div>
          <div>
            <p className="font-semibold text-base leading-tight">Call Support</p>
            <p className="text-sm text-white/80">Speak directly with our support team</p>
          </div>
        </div>

        <div className="flex-1 p-6 flex flex-col items-center justify-center gap-4 text-center overflow-y-auto">
          {call.state === 'IDLE' && (
            <>
              <Phone className="h-10 w-10 text-gray-500" />
              <p className="text-sm text-gray-600">Connecting to agent…</p>
            </>
          )}

          {(call.state === 'REQUESTING_PERMISSION' || call.state === 'CONNECTING') && (
            <p className="text-sm text-gray-600">Connecting to agent…</p>
          )}

          {call.state === 'RINGING' && <p className="text-sm text-gray-600">Calling agent…</p>}

          {call.state === 'CONNECTED' && (
            <div className="flex flex-col items-center gap-6 w-full">
              <div className="relative h-24 w-24">
                <div className="absolute inset-0 rounded-full bg-emerald-100 animate-pulse" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Headset className="h-11 w-11 text-emerald-600" />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-center gap-1.5 text-emerald-600 text-xs font-medium mb-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Connected
                </div>
                <p className="text-2xl font-semibold tabular-nums tracking-wide text-gray-900">
                  {formatDuration(call.durationSeconds)}
                </p>
              </div>
              <div className="flex gap-4">
                <button
                  type="button"
                  aria-label={call.muted ? 'Unmute' : 'Mute'}
                  aria-pressed={call.muted}
                  onClick={call.toggleMute}
                  className={`flex h-12 w-12 items-center justify-center rounded-full border transition ${
                    call.muted
                      ? 'bg-gray-800 border-gray-800 text-white'
                      : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {call.muted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
                </button>
                <button
                  type="button"
                  aria-label="End call"
                  onClick={call.endCall}
                  className="flex h-12 w-12 items-center justify-center rounded-full bg-red-600 text-white hover:bg-red-700 transition"
                >
                  <PhoneOff className="h-5 w-5" />
                </button>
              </div>
              {call.muted && <p className="text-xs text-gray-500">You're muted</p>}
            </div>
          )}

          {call.state === 'ENDING' && (
            <div className="flex flex-col items-center gap-3">
              <PhoneOff className="h-8 w-8 text-gray-400" />
              <p className="text-sm text-gray-600">Ending call…</p>
            </div>
          )}

          {call.state === 'ENDED' && (
            perInteractionEnabled && !ratingDismissed ? (
              <RatingPopup
                scale={ratingScale}
                {...(call.callSessionId != null && { callId: call.callSessionId })}
                tenantSlug={tenantSlug}
                onSubmitted={() => setRatingDismissed(true)}
                onDismiss={() => setRatingDismissed(true)}
              />
            ) : (
              <div className="flex flex-col items-center gap-2">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                  <PhoneOff className="h-5 w-5 text-gray-500" />
                </div>
                <p className="text-sm text-gray-600">Call ended.</p>
                {call.durationSeconds > 0 && (
                  <p className="text-xs text-gray-400">Duration: {formatDuration(call.durationSeconds)}</p>
                )}
              </div>
            )
          )}

          {call.state === 'FAILED' && (
            <div className="flex flex-col items-center w-full gap-5">
              {call.errorCode === 'NO_AGENTS' ? (
                <>
                  <div className="relative h-32 w-32">
                    <div className="absolute inset-0 rounded-full bg-violet-100" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Headset className="h-16 w-16 text-violet-300" />
                    </div>
                    <div className="absolute top-3 right-1 h-6 w-8 rounded-md bg-white shadow-sm flex items-center justify-center text-violet-400">
                      <MoreHorizontal className="h-4 w-4" />
                    </div>
                    <Sparkles className="absolute top-5 left-1.5 h-3 w-3 text-violet-300" />
                    <Sparkles className="absolute bottom-5 right-2 h-3.5 w-3.5 text-violet-300" />
                  </div>
                  <div className="text-center">
                    <p className="text-base font-semibold text-gray-900">All agents are busy right now 😥</p>
                    <p className="text-xs text-gray-600 mt-1 max-w-[260px]">
                      Our team is helping other customers. You can try again in a few minutes.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <PhoneOff className="h-10 w-10 text-red-600" />
                  <p className="text-sm text-gray-600">{ERROR_COPY[call.errorCode ?? 'UNEXPECTED_TERMINATION']}</p>
                </>
              )}

              <div className="flex flex-col w-full gap-3">
                <button
                  type="button"
                  className="w-full flex items-center justify-between gap-3 px-4 py-3.5 rounded-2xl border border-blue-600 text-blue-600 bg-white hover:bg-blue-50 transition"
                  onClick={() => void call.retry()}
                >
                  <div className="flex items-center gap-2">
                    <RotateCw className="h-5 w-5" />
                    <span className="font-medium">Try Again</span>
                  </div>
                  <ChevronRight className="h-5 w-5" />
                </button>

                <button
                  type="button"
                  className="w-full flex items-center justify-between gap-3 px-4 py-3.5 rounded-2xl bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:opacity-95 transition"
                  onClick={onContinueChat}
                >
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    <span className="font-medium">Continue Chat</span>
                  </div>
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>

              {call.errorCode === 'NO_AGENTS' && (
                <div className="w-full flex items-center gap-3 rounded-xl bg-violet-50 p-4 text-left">
                  <div className="h-9 w-9 rounded-full bg-white/70 flex items-center justify-center">
                    <Clock className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Average wait time: 2-4 minutes</p>
                    <p className="text-xs text-gray-600 mt-0.5">We appreciate your patience! 💜</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
